// X2 — pure Auswahl-/Label-Logik des „Nächster Termin"-Chips. BEWUSST in einem
// JSX-freien .ts-Modul (kein React-Import), damit der Dry-Run (x2-label-dryrun.mts,
// via `node --experimental-strip-types`) GENAU DIESE Funktionen importieren und
// testen kann — node strip-types transformiert kein JSX (Muster: parseGermanDate.ts
// ← p2-16-parser.mts). NextMeetingChip.tsx importiert von hier.
//
// DE/EN: Diese Datei bleibt IMPORTFREI — auch ohne den Katalog. Der Dry-Run lädt
// sie als einzelne Datei; ein Import von ../i18n/lang würde ihn zerreissen
// (gemessen: extensionsloser Import → ERR_MODULE_NOT_FOUND, über lang.ts →
// ERR_UNSUPPORTED_DIR_IMPORT auf src/i18n/de). Die vier Beschriftungen werden
// deshalb HEREINGEREICHT; ohne Angabe gilt Deutsch, damit der Dry-Run dieselben
// Zeichenketten prüft wie bisher. Den Katalog steckt NextMeetingChip.tsx dazu.

// Minimales Event-Interface, das der Chip braucht (D2). BEWUSST NICHT das CalEvent
// aus CalendarMs.tsx wiederverwendet — der Chip liest nur diese Felder und behandelt
// `join_url` optional/defensiv, damit der Beitreten-Link automatisch aufleuchtet,
// sobald Slice C das Feld in die /calendar/view-Projektion aufnimmt (heute fehlt es).
export interface ChipEvent {
  subject: string;
  /** Offset-lose naive Lokalzeit (Graph mit `Prefer: outlook.timezone`),
   *  z. B. "2026-07-08T09:00:00.0000000". Nativ via `new Date(iso)` = Lokalzeit. */
  start: string;
  end: string;
  is_all_day?: boolean;
  is_cancelled?: boolean;
  join_url?: string | null;
  /** Ort aus der /calendar/view-Projektion (email-service sb_calendar.py:152).
   *  Bei Teams-Terminen steht hier „Microsoft Teams-Besprechung" — genau die
   *  Zeile, die Outlook in seiner Terminliste als Untertitel zeigt. */
  location?: string | null;
}

/** Die Beschriftungen, die diese Datei zusammensetzt. Struktur-gleich zu
 *  `Katalog["shared"]["termine"]` — der Chip reicht den Katalog direkt durch. */
export interface TerminTexte {
  laeuft: string;
  inMin: (n: number) => string;
  umUhr: (zeit: string) => string;
  heute: string;
  morgen: string;
}

/** Voreinstellung = Deutsch. Sie steht hier und nicht im Katalog, weil diese
 *  Datei importfrei bleiben muss (s. Kopf). */
export const TEXTE_DE: TerminTexte = {
  laeuft: "läuft",
  inMin: (n) => `in ${n} Min`,
  umUhr: (zeit) => `um ${zeit}`,
  heute: "Heute",
  morgen: "Morgen",
};

/**
 * Nächster relevanter Termin = nicht abgesagt, nicht ganztägig, noch nicht beendet
 * (`end > now`), aufsteigend nach `start`, erster. `null` wenn kein Kandidat.
 */
export function pickNextMeeting(events: ChipEvent[], now: Date): ChipEvent | null {
  const nowMs = now.getTime();
  const candidates = events
    .filter(
      (e) => !e.is_cancelled && !e.is_all_day && new Date(e.end).getTime() > nowMs
    )
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  return candidates[0] ?? null;
}

/**
 * Countdown-/Zustandstext eines Termins relativ zu `now`. Reihenfolge zählt:
 * „läuft" MUSS vor „in {n} Min" geprüft werden, sonst würde ein laufender Termin
 * (Differenz ≤ 0) fälschlich „in 1 Min" zeigen.
 *   - start <= now < end   → "läuft"
 *   - start - now < 60 min  → "in {n} Min"  (aufgerundet auf ganze Minuten)
 *   - sonst                 → "um HH:MM"
 */
export function meetingLabel(
  ev: ChipEvent,
  now: Date,
  texte: TerminTexte = TEXTE_DE
): string {
  const nowMs = now.getTime();
  const startMs = new Date(ev.start).getTime();
  const endMs = new Date(ev.end).getTime();
  if (startMs <= nowMs && nowMs < endMs) return texte.laeuft;
  const diffMs = startMs - nowMs;
  if (diffMs < 60 * 60 * 1000) {
    const mins = Math.max(1, Math.ceil(diffMs / 60_000));
    return texte.inMin(mins);
  }
  // Die UHRZEIT bleibt in jeder Oberflächensprache deutsch formatiert
  // (docs/i18n.md §2); übersetzt wird nur das Wort davor.
  return texte.umUhr(
    new Date(ev.start).toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
}

// ── Sidebar-Terminliste (7 Tage, nach Tagen gruppiert) ───────────────────────
// Tim Bentler, 07.08.2026: „dass ich z. B. die Termine der nächsten 7 Tage sehe
// … 4-5 Einträge untereinander und den Rest durchscrollen". Vorbild ist die
// Outlook-Terminliste: Tagesüberschrift, je Zeile Uhrzeit + Betreff + Ort.

/** Sichtfenster der Liste: heute + 6 weitere Tage = 7 Kalendertage.
 *  Serverseitig unkritisch (email-service MAX_WINDOW_DAYS = 31). */
export const LIST_DAYS = 7;

/** Ein Tag der Liste. `key` ist die lokale Kalenderdatum-ID (stabil für React). */
export interface DayGroup {
  key: string;
  label: string;
  events: ChipEvent[];
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function dayId(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Ende des Abfrage-/Anzeige-Fensters: Mitternacht nach dem letzten Tag.
 *  BEWUSST auf Tagesgrenze statt `now + 7*24h`: sonst wäre der siebte Tag je
 *  nach Uhrzeit halb abgeschnitten und die letzte Gruppe unvollständig. */
export function listWindowEnd(now: Date, days: number = LIST_DAYS): Date {
  const s = startOfDay(now);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + days);
}

/**
 * Überschrift einer Tagesgruppe: „Heute" / „Morgen" / sonst Wochentag + Datum
 * („Montag, 10.08."). Das Datum bleibt dran, weil ein 7-Tage-Fenster denselben
 * Wochentag zweimal enthalten kann (heute Fr → nächster Fr am Tag 7).
 * Differenz über Tagesgrenzen, nicht über Stunden (DST-fest: 23 h/25 h runden
 * auf 1 Tag).
 */
export function dayLabel(
  day: Date,
  now: Date,
  texte: TerminTexte = TEXTE_DE
): string {
  const diff = Math.round(
    (startOfDay(day).getTime() - startOfDay(now).getTime()) / 86_400_000
  );
  if (diff === 0) return texte.heute;
  if (diff === 1) return texte.morgen;
  // Wochentag + Datum bleiben deutsch (docs/i18n.md §2).
  return day.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  });
}

/** Uhrzeit-Spalte der Liste („08:30"). */
export function timeLabel(ev: ChipEvent): string {
  return new Date(ev.start).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Untertitel-Zeile einer Termin-Zeile = der Ort, ohne den Hersteller-Vorsatz.
 * Graph liefert bei Online-Terminen „Microsoft Teams-Besprechung"; in einer
 * 240-px-Seitenleiste wurde daraus in JEDER Zeile ein abgeschnittenes
 * „Microsoft Teams-Bespre…". Das „Microsoft " sagt nichts, was
 * „Teams-Besprechung" nicht auch sagt — also fällt der Vorsatz weg und die
 * Zeile passt vollständig. Andere Orte bleiben unverändert.
 */
export function ortLabel(ev: ChipEvent): string {
  const ort = (ev.location || "").trim();
  return ort.startsWith("Microsoft ") ? ort.slice("Microsoft ".length) : ort;
}

/** „Jetzt relevant": läuft gerade oder startet innerhalb der nächsten Stunde.
 *  Nur diese Zeilen bekommen Countdown + „Beitreten" — ein Beitreten-Link an
 *  einem Termin vom Donnerstag wäre Rauschen, kein Angebot. */
export function isImminent(
  ev: ChipEvent,
  now: Date,
  withinMs: number = 60 * 60 * 1000
): boolean {
  const startMs = new Date(ev.start).getTime();
  const nowMs = now.getTime();
  return startMs - nowMs < withinMs;
}

/**
 * Die Termine des Fensters, nach Kalendertagen gruppiert und aufsteigend
 * sortiert. Gefiltert wie bisher beim Chip: nicht abgesagt, nicht ganztägig,
 * noch nicht beendet (`end > now`) — plus Start vor dem Fensterende.
 * Ein Termin, der VOR heute begann und noch läuft (Übertrag über Mitternacht),
 * wird auf „Heute" geklemmt; sonst entstünde eine Gruppe in der Vergangenheit.
 */
export function groupUpcoming(
  events: ChipEvent[],
  now: Date,
  texte: TerminTexte = TEXTE_DE,
  days: number = LIST_DAYS
): DayGroup[] {
  const nowMs = now.getTime();
  const endMs = listWindowEnd(now, days).getTime();
  const today = startOfDay(now);

  const upcoming = events
    .filter((e) => !e.is_cancelled && !e.is_all_day)
    .filter((e) => {
      const s = new Date(e.start).getTime();
      const en = new Date(e.end).getTime();
      return Number.isFinite(s) && Number.isFinite(en) && en > nowMs && s < endMs;
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const groups: DayGroup[] = [];
  const byKey = new Map<string, DayGroup>();
  for (const e of upcoming) {
    const evDay = startOfDay(new Date(e.start));
    const day = evDay.getTime() < today.getTime() ? today : evDay;
    const key = dayId(day);
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: dayLabel(day, now, texte), events: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.events.push(e);
  }
  return groups;
}
